/**
 * Attachment Service - Unified file upload and management
 * Handles uploading, downloading, and deleting attachments across all entity types
 */

import { supabase } from '@/lib/supabase';
import {
  type Attachment,
  type AttachmentType,
  type ParentEntityType,
  type UploadProgress,
  ENTITY_TYPE_TO_BUCKET,
} from '@/data/attachmentTypes';

type AttachmentRow = {
  id: string;
  parent_type?: string;
  parent_id?: string;
  attachment_type?: string;
  file_name?: string;
  file_path?: string;
  mime_type?: string | null;
  file_size?: number | null;
  created_by?: string;
  created_at?: string;
  notes?: string | null;
  parentType?: string;
  parentId?: string;
  attachmentType?: string;
  fileName?: string;
  filePath?: string;
  mimeType?: string | null;
  fileSize?: number | null;
  createdBy?: string;
  createdAt?: string;
};

function toAttachment(row: AttachmentRow): Attachment {
  return {
    id: row.id,
    parentType: (row.parentType ?? row.parent_type ?? 'job') as ParentEntityType,
    parentId: String(row.parentId ?? row.parent_id ?? ''),
    attachmentType: (row.attachmentType ?? row.attachment_type ?? 'other') as AttachmentType,
    fileName: String(row.fileName ?? row.file_name ?? ''),
    filePath: String(row.filePath ?? row.file_path ?? ''),
    mimeType: (row.mimeType ?? row.mime_type ?? undefined) ?? undefined,
    fileSize: (row.fileSize ?? row.file_size ?? undefined) ?? undefined,
    createdBy: String(row.createdBy ?? row.created_by ?? ''),
    createdAt: String(row.createdAt ?? row.created_at ?? new Date().toISOString()),
    notes: (row.notes ?? undefined) ?? undefined,
  };
}

/**
 * Upload a single file attachment
 */
export async function uploadAttachment(
  file: File,
  parentType: ParentEntityType,
  parentId: string,
  attachmentType: AttachmentType,
  createdBy: string,
  notes?: string,
): Promise<Attachment> {
  // Non-image files (docs, PDFs, etc.) must go to job-documents — job-photos
  // only accepts image MIME types and will reject everything else with a 400.
  const isImage = file.type.startsWith('image/');
  const bucket = (ENTITY_TYPE_TO_BUCKET[parentType] === 'job-photos' && !isImage)
    ? 'job-documents'
    : ENTITY_TYPE_TO_BUCKET[parentType];

  // Generate unique path: {parentType}/{parentId}/{timestamp}-{filename}
  const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
  const filePath = `${parentType}/${parentId}/${Date.now()}-${sanitizedFileName}`;
  
  // Upload to Supabase Storage
  const { data: uploadData, error: uploadError } = await supabase.storage
    .from(bucket)
    .upload(filePath, file, {
      cacheControl: '3600',
      upsert: false,
    });

  if (uploadError) {
    console.error('Storage upload error:', uploadError);
    throw new Error(`Failed to upload file: ${uploadError.message}`);
  }

  // Store the storage path (bucket::path) so signed URLs can be generated on read.
  // Never store publicUrl — buckets are private and public URL construction fails.
  const storagePath = `${bucket}::${filePath}`;

  // Create database record
  const { data: record, error: dbError } = await supabase
    .from('attachments')
    .insert({
      parent_type: parentType,
      parent_id: parentId,
      attachment_type: attachmentType,
      file_name: file.name,
      file_path: storagePath,
      mime_type: file.type,
      file_size: file.size,
      created_by: createdBy,
      ...(notes ? { notes } : {}),
    })
    .select()
    .single();

  if (dbError) {
    console.error('Database insert error:', dbError);
    // Clean up the uploaded file if DB insert fails
    await supabase.storage.from(bucket).remove([filePath]);
    throw new Error(`Failed to save attachment record: ${dbError.message}`);
  }

  return toAttachment(record as AttachmentRow);
}

/**
 * Upload multiple files with progress tracking
 */
export async function uploadMultiple(
  files: File[],
  parentType: ParentEntityType,
  parentId: string,
  attachmentType: AttachmentType,
  createdBy: string,
  onProgress?: (progress: UploadProgress) => void
): Promise<Attachment[]> {
  const results: Attachment[] = [];

  for (let i = 0; i < files.length; i++) {
    const file = files[i];

    const progressUpdate: UploadProgress = {
      fileName: file.name,
      progress: 0,
      status: 'uploading',
    };
    onProgress?.(progressUpdate);

    try {
      const attachment = await uploadAttachment(
        file,
        parentType,
        parentId,
        attachmentType,
        createdBy
      );
      results.push(attachment);

      onProgress?.({
        fileName: file.name,
        progress: 100,
        status: 'complete',
      });
    } catch (error) {
      onProgress?.({
        fileName: file.name,
        progress: 0,
        status: 'error',
        error: (error as Error).message,
      });
    }
  }

  return results;
}

/**
 * Delete an attachment (removes from storage and database)
 */
export async function deleteAttachment(id: string): Promise<void> {
  // Fetch attachment record to get file path
  const { data: attachment, error: fetchError } = await supabase
    .from('attachments')
    .select('file_path, parent_type')
    .eq('id', id)
    .single();

  if (fetchError) {
    console.error('Failed to fetch attachment:', fetchError);
    throw new Error('Attachment not found');
  }

  // Extract bucket and path from URL
  const bucket = ENTITY_TYPE_TO_BUCKET[attachment.parent_type as ParentEntityType];
  
  // Extract just the path portion after bucket name
  const urlParts = attachment.file_path.split(`/storage/v1/object/public/${bucket}/`);
  const filePath = urlParts[1] || urlParts[0];

  // Delete from storage
  const { error: storageError } = await supabase.storage
    .from(bucket)
    .remove([filePath]);

  if (storageError) {
    console.error('Failed to delete from storage:', storageError);
  }

  // Delete database record
  const { error: dbError } = await supabase
    .from('attachments')
    .delete()
    .eq('id', id);

  if (dbError) {
    console.error('Failed to delete attachment record:', dbError);
    throw new Error(`Failed to delete attachment: ${dbError.message}`);
  }
}

/**
 * Get all attachments for a specific entity
 */
export async function getAttachments(
  parentType: ParentEntityType,
  parentId: string
): Promise<Attachment[]> {
  const { data, error } = await supabase
    .from('attachments')
    .select('*')
    .eq('parent_type', parentType)
    .eq('parent_id', parentId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Failed to fetch attachments:', error);
    throw new Error(`Failed to load attachments: ${error.message}`);
  }

  return (data as AttachmentRow[] | null)?.map(toAttachment) ?? [];
}

/**
 * Get a single attachment by ID
 */
export async function getAttachment(id: string): Promise<Attachment | null> {
  const { data, error } = await supabase
    .from('attachments')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      return null; // Record not found
    }
    console.error('Failed to fetch attachment:', error);
    throw new Error(`Failed to load attachment: ${error.message}`);
  }

  return toAttachment(data as AttachmentRow);
}

/**
 * Get attachments filtered by type
 */
export async function getAttachmentsByType(
  parentType: ParentEntityType,
  parentId: string,
  attachmentType: AttachmentType
): Promise<Attachment[]> {
  const { data, error } = await supabase
    .from('attachments')
    .select('*')
    .eq('parent_type', parentType)
    .eq('parent_id', parentId)
    .eq('attachment_type', attachmentType)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Failed to fetch attachments by type:', error);
    throw new Error(`Failed to load attachments: ${error.message}`);
  }

  return (data as AttachmentRow[] | null)?.map(toAttachment) ?? [];
}

/**
 * Return signed photo URLs for a job — for use in dispatch brief snapshots.
 * Filters parent_type='job', attachment_type='photo'. Uses a 1-year TTL so
 * URLs stored in brief_snapshot remain valid for the life of the opportunity.
 * job-photos is a private bucket; public URL construction does not work.
 */
export async function getJobPhotoUrls(jobId: string): Promise<string[]> {
  const photos = await getJobPhotoMeta(jobId);
  return photos.map((p) => p.src);
}

/**
 * Return signed photo URLs + captions for a job — for portal offer brief_snapshot.
 * caption = attachment.notes if set, else file_name without extension.
 * Uses a 1-year TTL so URLs remain valid for the life of the opportunity.
 */
export async function getJobPhotoMeta(jobId: string): Promise<{ src: string; caption: string }[]> {
  const { data: rows, error: rowsError } = await supabase
    .from('attachments')
    .select('file_path, file_name, notes')
    .eq('parent_type', 'job')
    .eq('parent_id', jobId)
    .eq('attachment_type', 'photo')
    .order('created_at', { ascending: true });

  if (rowsError) {
    console.warn('[getJobPhotoMeta] failed to fetch rows:', rowsError);
    return [];
  }

  if (!rows || rows.length === 0) return [];

  const bucket = 'job-photos';
  const marker = `/storage/v1/object/public/${bucket}/`;
  type PhotoRow = { file_path: string; file_name: string | null; notes: string | null };
  const typed = rows as PhotoRow[];

  const paths = typed.map((r) => {
    const p = r.file_path;
    return p.includes(marker) ? p.split(marker)[1] : p;
  }).filter(Boolean);

  if (paths.length === 0) return [];

  // 1-year TTL — stored in brief_snapshot at dispatch time; must outlive the opportunity.
  const { data: signed, error: signError } = await supabase.storage
    .from(bucket)
    .createSignedUrls(paths, 60 * 60 * 24 * 365);

  if (signError) {
    console.warn('[getJobPhotoMeta] failed to create signed URLs:', signError);
    return [];
  }

  return (signed ?? [])
    .map((d, i) => {
      if (!d.signedUrl) return null;
      const row = typed[i];
      const caption = row?.notes?.trim()
        || (row?.file_name ? row.file_name.replace(/\.[^/.]+$/, '') : '');
      return { src: d.signedUrl as string, caption };
    })
    .filter((x): x is { src: string; caption: string } => x !== null);
}

/**
 * Return signed site-documentation photos for a job by resolving its linked quote.
 * Wraps getQuoteSitePhotoMeta — looks up the quote via jobs.linked_job_id → quotes.id.
 * Use this from pages that only have a job ID (e.g. JobOfferReviewWorkspace).
 */
export async function getJobSitePhotoMeta(jobId: string): Promise<{ src: string; caption: string }[]> {
  // Find the quote whose linked_job_id matches this job UUID or job_number
  const { data: quoteRows, error } = await supabase
    .from('quotes')
    .select('id')
    .eq('linked_job_id', jobId)
    .limit(1);

  if (error || !quoteRows || quoteRows.length === 0) {
    console.warn('[getJobSitePhotoMeta] no linked quote found for job:', jobId);
    return [];
  }

  return getQuoteSitePhotoMeta((quoteRows[0] as { id: string }).id);
}

const SITE_PHOTO_CATEGORY_LABEL: Record<string, string> = {
  project_area: "Full Project Area",
  measurement:  "Measurement Reference",
  obstruction:  "Obstructions / Special Conditions",
};

/**
 * Return signed site-documentation photos for a quote — for use in offer brief_snapshot.
 * Photos are stored in the job-documents bucket under parent_type='quote'.
 * notes field contains "site_photo:<category>" — used to derive the caption.
 * Uses a 1-year TTL so URLs remain valid for the life of the opportunity.
 */
export async function getQuoteSitePhotoMeta(quoteId: string): Promise<{ src: string; caption: string }[]> {
  const { data: rows, error: rowsError } = await supabase
    .from('attachments')
    .select('file_path, file_name, notes')
    .eq('parent_type', 'quote')
    .eq('parent_id', quoteId)
    .eq('attachment_type', 'photo')
    .order('created_at', { ascending: true });

  if (rowsError) {
    console.warn('[getQuoteSitePhotoMeta] failed to fetch rows:', rowsError);
    return [];
  }
  if (!rows || rows.length === 0) return [];

  const bucket = 'job-documents';
  const marker = `/storage/v1/object/public/${bucket}/`;
  type PhotoRow = { file_path: string; file_name: string | null; notes: string | null };
  const typed = rows as PhotoRow[];

  const paths = typed.map((r) => {
    const p = r.file_path;
    return p.includes(marker) ? p.split(marker)[1] : p;
  }).filter(Boolean);

  if (paths.length === 0) return [];

  const { data: signed, error: signError } = await supabase.storage
    .from(bucket)
    .createSignedUrls(paths, 60 * 60 * 24 * 365);

  if (signError) {
    console.warn('[getQuoteSitePhotoMeta] failed to create signed URLs:', signError);
    return [];
  }

  return (signed ?? [])
    .map((d, i) => {
      if (!d.signedUrl) return null;
      const row = typed[i];
      // notes = "site_photo:project_area" → caption "Full Project Area"
      const category = row?.notes?.startsWith('site_photo:')
        ? row.notes.replace('site_photo:', '')
        : '';
      const caption = SITE_PHOTO_CATEGORY_LABEL[category]
        ?? row?.notes?.trim()
        ?? (row?.file_name ? row.file_name.replace(/\.[^/.]+$/, '') : '');
      return { src: d.signedUrl as string, caption };
    })
    .filter((x): x is { src: string; caption: string } => x !== null);
}

/**
 * Count attachments for an entity
 */
export async function countAttachments(
  parentType: ParentEntityType,
  parentId: string
): Promise<number> {
  const { count, error } = await supabase
    .from('attachments')
    .select('*', { count: 'exact', head: true })
    .eq('parent_type', parentType)
    .eq('parent_id', parentId);

  if (error) {
    console.error('Failed to count attachments:', error);
    return 0;
  }

  return count || 0;
}

/**
 * Get download URL for an attachment (creates signed URL for private files)
 */
export async function getDownloadUrl(attachmentId: string, expiresIn = 3600): Promise<string> {
  // First get the attachment to know the bucket
  const attachment = await getAttachment(attachmentId);
  if (!attachment) {
    throw new Error('Attachment not found');
  }

  const rawPath = attachment.filePath;
  if (!rawPath) {
    throw new Error('Attachment file path is missing');
  }

  // Resolve bucket + path from stored value.
  // Three formats supported:
  //   1. "bucket::path/to/file"  — private upload (current format)
  //   2. "path/to/file"          — bare storage path (legacy)
  //   3. "https://…/public/bucket/path" — old public URL (legacy)
  let bucket: string;
  let filePath: string;
  if (rawPath.includes('::')) {
    [bucket, filePath] = rawPath.split('::', 2);
  } else {
    bucket = ENTITY_TYPE_TO_BUCKET[attachment.parentType as ParentEntityType];
    const marker = `/storage/v1/object/public/${bucket}/`;
    filePath = rawPath.includes(marker) ? rawPath.split(marker)[1] : rawPath;
  }
  if (!filePath) {
    throw new Error('Attachment storage path is invalid');
  }

  // Generate signed URL
  const { data, error } = await supabase.storage
    .from(bucket)
    .createSignedUrl(filePath, expiresIn);

  if (error) {
    console.error('Failed to create signed URL:', error);
    // Return public URL as fallback
    return rawPath;
  }

  return data.signedUrl ?? rawPath;
}

/**
 * Upload a file to a private (non-public) bucket.
 * Stores the storage PATH in file_path (not a public URL).
 * Use createSignedAttachmentUrl() to generate a temporary display URL.
 */
export async function uploadPrivateAttachment(
  file: File,
  bucket: string,
  parentType: ParentEntityType,
  parentId: string,
  attachmentType: AttachmentType,
  createdBy: string,
  notes?: string,
): Promise<{ id: string; file_path: string; file_name: string; created_at: string }> {
  const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
  const storagePath = `${parentType}/${parentId}/${Date.now()}-${sanitizedFileName}`;

  const { error: uploadError } = await supabase.storage
    .from(bucket)
    .upload(storagePath, file, { cacheControl: '3600', upsert: false });

  if (uploadError) {
    console.error('Private storage upload error:', uploadError);
    throw new Error(`Failed to upload file: ${uploadError.message}`);
  }

  // Store the storage path (not a public URL) so we can generate signed URLs later
  const { data: record, error: dbError } = await supabase
    .from('attachments')
    .insert({
      parent_type: parentType,
      parent_id: parentId,
      attachment_type: attachmentType,
      file_name: file.name,
      file_path: storagePath,
      mime_type: file.type,
      file_size: file.size,
      created_by: createdBy,
      ...(notes ? { notes } : {}),
    })
    .select()
    .single();

  if (dbError) {
    console.error('Database insert error:', dbError);
    await supabase.storage.from(bucket).remove([storagePath]);
    throw new Error(`Failed to save attachment record: ${dbError.message}`);
  }

  return record as { id: string; file_path: string; file_name: string; created_at: string };
}

/**
 * Generate a temporary signed URL for a private-bucket attachment.
 * Default expiry: 1 hour.
 */
export async function createSignedAttachmentUrl(
  storagePath: string,
  bucket: string,
  expiresIn = 3600,
): Promise<string> {
  // uploadAttachment stores paths as "bucket::path" — strip the prefix before signing
  const cleanPath = storagePath.includes('::') ? storagePath.split('::', 2)[1] : storagePath;
  const { data, error } = await supabase.storage
    .from(bucket)
    .createSignedUrl(cleanPath, expiresIn);

  if (error || !data?.signedUrl) {
    throw new Error('Failed to generate signed URL');
  }
  return data.signedUrl;
}

/**
 * Delete a private-bucket attachment (storage path stored directly in file_path).
 */
export async function deletePrivateAttachment(id: string, bucket: string): Promise<void> {
  const { data: attachment, error: fetchError } = await supabase
    .from('attachments')
    .select('file_path')
    .eq('id', id)
    .single();

  if (fetchError) throw new Error('Attachment not found');

  await supabase.storage.from(bucket).remove([attachment.file_path]);

  const { error: dbError } = await supabase
    .from('attachments')
    .delete()
    .eq('id', id);

  if (dbError) throw new Error(`Failed to delete attachment: ${dbError.message}`);
}

/**
 * Batch delete multiple attachments
 */
export async function deleteMultipleAttachments(ids: string[]): Promise<void> {
  // Get all attachments to determine storage paths
  const { data: attachments, error: fetchError } = await supabase
    .from('attachments')
    .select('id, file_path, parent_type')
    .in('id', ids);

  if (fetchError) {
    throw new Error(`Failed to fetch attachments: ${fetchError.message}`);
  }

  // Group by bucket for efficient deletion
  const bucketPaths: Record<string, string[]> = {};
  
  for (const attachment of attachments || []) {
    const bucket = ENTITY_TYPE_TO_BUCKET[attachment.parent_type as ParentEntityType];
    const urlParts = attachment.file_path.split(`/storage/v1/object/public/${bucket}/`);
    const filePath = urlParts[1] || urlParts[0];
    
    if (!bucketPaths[bucket]) {
      bucketPaths[bucket] = [];
    }
    bucketPaths[bucket].push(filePath);
  }

  // Delete from storage buckets
  for (const [bucket, paths] of Object.entries(bucketPaths)) {
    await supabase.storage.from(bucket).remove(paths);
  }

  // Delete database records
  const { error: dbError } = await supabase
    .from('attachments')
    .delete()
    .in('id', ids);

  if (dbError) {
    throw new Error(`Failed to delete attachments: ${dbError.message}`);
  }
}
